<script setup>
  import { ref, computed, provide, onMounted, nextTick } from "vue";
  import { useStore } from "vuex";

  /* SIDEBAR & PROMPT */
  import Sidebar from "@/components/WEB/sidebar.vue";
  import CreateWebsitePrompt from "@/components/WEB/CreateWebsitePrompt.vue";

  /* STUDIO CMS COMPONENTS */
  import WebsiteSettings from "@/components/WEB/WebsiteSettings.vue";
  import ThemeSettings from "@/components/WEB/ThemeSettings.vue";
  import HeroSettings from "@/components/WEB/HeroSettings.vue";
  import dashboard from "@/components/WEB/HeroDasboard.vue";
  import Notifications from "@/components/WEB/NoticeCms.vue";
  import SiteWidgetsManager from "@/components/WEB/SiteWidgetsManager.vue";
  import Academic from "@/components/WEB/Academic.vue";
  import Campus from "@/components/WEB/Campus.vue";
  import about from "@/components/WEB/SchoolAbout.vue";
  import Principal from "@/components/WEB/Principal.vue";
  import Futures from "@/components/WEB/Futures&Parents.vue";
  import ProspectusFooterEditor from "@/components/WEB/ProspectusFooterEditor.vue";

  /* LUCIDE ICONS */
  import {
    LayoutDashboard,
    Settings,
    Palette,
    Images,
    School,
    Users,
    GraduationCap,
    Building2,
    ImageIcon,
    CalendarDays,
    Newspaper,
    MessageSquareQuote,
    Search,
    Share2,
    Phone,
    Globe,
    Menu,
    ExternalLink,
    Copy,
    Check,
    PanelLeftClose,
    PanelLeftOpen,
  } from "lucide-vue-next";

  import { useGlobalToast } from "@/utils/useGlobalToast";

  /* =========================================
   STORE & TOAST
  ========================================= */
  const { showToast } = useGlobalToast();
  const store = useStore();

  const STORAGE_KEY = "educanium_active_sidebar_tab";
  const COLLAPSED_STORAGE_KEY = "educanium_sidebar_collapsed";

  /* =========================================
   STATE
  ========================================= */
  const activePage = ref("dashboard");
  const isInitializing = ref(true);
  const isSidebarCollapsed = ref(false);
  const isMobileSidebarOpen = ref(false);
  const isCopied = ref(false);

  const scrollContainerRef = ref(null);
  const pillStripRef = ref(null);

  /* =========================================
   COMPUTED STORE GETTERS
  ========================================= */
  const schoolData = computed(() => store.state.schools?.[0] || null);
  const websiteDetails = computed(
    () => store.state.websiteDetails || store.getters["getWebsiteDetails"] || null,
  );

  const schoolName = computed(
    () => websiteDetails.value?.schoolName || schoolData.value?.schoolName || "Educanium School",
  );
  const schoolSlug = computed(
    () => websiteDetails.value?.slug || schoolData.value?.slug || "portal",
  );
  const schoolLogo = computed(
    () => websiteDetails.value?.logo || schoolData.value?.logo || "",
  );
  const isLive = computed(
    () => websiteDetails.value?.websiteEnabled ?? schoolData.value?.websiteEnabled ?? true,
  );

  const schoolInitials = computed(() => {
    return (
      schoolName.value
        ?.split(" ")
        ?.map((w) => w[0])
        ?.join("")
        ?.substring(0, 2)
        ?.toUpperCase() || "ED"
    );
  });

  const fullPublicUrl = computed(() => {
    const slug = schoolSlug.value;
    if (!slug) return "#";
    if (
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1")
    ) {
      const port = window.location.port ? `:${window.location.port}` : "";
      return `${window.location.protocol}//${window.location.hostname}${port}/site/${slug}`;
    }
    return `https://${slug}.educanium.com`;
  });

  /* =========================================
   WEBSITE STATUS CHECK
  ========================================= */
  const isWebsiteCreated = computed(() => {
    return Boolean(
      websiteDetails.value?.websiteEnabled ||
      schoolData.value?.websiteEnabled ||
      websiteDetails.value?.slug ||
      schoolData.value?.slug,
    );
  });

  /* =========================================
   COMPONENT MAP
  ========================================= */
  const componentMap = {
    dashboard: dashboard,
    "website-settings": WebsiteSettings,
    "theme-settings": ThemeSettings,
    "hero-section": HeroSettings,
    "about-school": about,
    leadership: Principal,
    academics: Academic,
    facilities: SiteWidgetsManager,
    gallery: Campus,
    events: Futures,
    notifications: Notifications,
    "campus-infrastructure": Campus,
    "seo-settings": WebsiteSettings,
    "social-links": ProspectusFooterEditor,
    "contact-details": WebsiteSettings,
    "domain-settings": WebsiteSettings,
  };

  /* =========================================
   PAGE METADATA
  ========================================= */
  const pageMetaMap = {
    dashboard: {
      title: "Studio Command Center",
      category: "Overview",
      icon: LayoutDashboard,
      description: "Live portal health, template identity, and readiness metrics.",
    },
    "website-settings": {
      title: "Branding & School Details",
      category: "Overview",
      icon: Settings,
      description: "Manage school identity, badges, and institutional attributes.",
    },
    "theme-settings": {
      title: "Theme & Visual Identity",
      category: "Overview",
      icon: Palette,
      description: "Configure colors, styling presets, and layout aesthetics.",
    },
    "hero-section": {
      title: "Hero Banner & Carousel",
      category: "Content",
      icon: Images,
      description: "Captivating headlines, call-to-actions, and background slides.",
    },
    "about-school": {
      title: "About School & History",
      category: "Content",
      icon: School,
      description: "Mission, vision, legacy, and founder's statement.",
    },
    leadership: {
      title: "Principal & Leadership",
      category: "Content",
      icon: Users,
      description: "Key administration desk, principal's message, and credentials.",
    },
    academics: {
      title: "Academic Programs",
      category: "Content",
      icon: GraduationCap,
      description: "Curriculum tiers, board affiliations, and pedagogical streams.",
    },
    facilities: {
      title: "Facilities & Amenities",
      category: "Content",
      icon: Building2,
      description: "Laboratories, sports arenas, libraries, and transport fleets.",
    },
    gallery: {
      title: "Campus Photo & Video Gallery",
      category: "Media",
      icon: ImageIcon,
      description: "Curated event highlights, campus infrastructure, and photo tours.",
    },
    events: {
      title: "Events & Annual Calendar",
      category: "Media",
      icon: CalendarDays,
      description: "Upcoming student exhibitions, sports meets, and parent orientations.",
    },
    notifications: {
      title: "Circulars & Notices",
      category: "Media",
      icon: Newspaper,
      description: "Live circulars, downloadable PDFs, and urgent campus alerts.",
    },
    "campus-infrastructure": {
      title: "Campus Infrastructure",
      category: "Media",
      icon: MessageSquareQuote,
      description: "Architectural tours, modern amenities, and tech-enabled classrooms.",
    },
    "seo-settings": {
      title: "SEO & Social OpenGraph",
      category: "Config",
      icon: Search,
      description: "Search engine indexing, meta descriptions, and Google preview.",
    },
    "social-links": {
      title: "Prospectus & Footer",
      category: "Config",
      icon: Share2,
      description: "Digital admission prospectus, social handles, and footer links.",
    },
    "contact-details": {
      title: "Contact & Location",
      category: "Config",
      icon: Phone,
      description: "Campus GPS coordinates, phone lines, and visitor hours.",
    },
    "domain-settings": {
      title: "Custom Domain & SSL",
      category: "Config",
      icon: Globe,
      description: "CNAME routing, SSL certification, and custom domain mapping.",
    },
  };

  const activeMeta = computed(() => {
    return pageMetaMap[activePage.value] || pageMetaMap.dashboard;
  });

  const currentComponent = computed(() => {
    return componentMap[activePage.value] || dashboard;
  });

  /* =========================================
   MOBILE QUICK PILLS
  ========================================= */
  const mobilePillItems = [
    { id: "dashboard", label: "Overview", icon: LayoutDashboard },
    { id: "website-settings", label: "Branding", icon: Settings },
    { id: "theme-settings", label: "Theme", icon: Palette },
    { id: "hero-section", label: "Hero Banner", icon: Images },
    { id: "about-school", label: "About", icon: School },
    { id: "leadership", label: "Leadership", icon: Users },
    { id: "academics", label: "Academics", icon: GraduationCap },
    { id: "facilities", label: "Facilities", icon: Building2 },
    { id: "gallery", label: "Gallery", icon: ImageIcon },
    { id: "events", label: "Events", icon: CalendarDays },
    { id: "notifications", label: "Notices", icon: Newspaper },
    { id: "campus-infrastructure", label: "Campus", icon: MessageSquareQuote },
    { id: "seo-settings", label: "SEO", icon: Search },
    { id: "social-links", label: "Prospectus", icon: Share2 },
    { id: "contact-details", label: "Contact", icon: Phone },
    { id: "domain-settings", label: "Domain", icon: Globe },
  ];

  /* =========================================
   ACTIONS
  ========================================= */
  const changePage = (page) => {
    if (!componentMap[page]) {
      console.warn(`Invalid page: ${page}`);
      return;
    }

    activePage.value = page;

    try {
      localStorage.setItem(STORAGE_KEY, page);
    } catch (error) {
      console.error("Failed to save active sidebar tab:", error);
    }

    // Scroll main viewport container to top
    if (scrollContainerRef.value) {
      scrollContainerRef.value.scrollTo({ top: 0, behavior: "smooth" });
    }

    // Smoothly scroll the tapped pill into view on mobile
    nextTick(() => {
      const activeEl = pillStripRef.value?.querySelector(`[data-pill-id="${page}"]`);
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      }
    });
  };

  const toggleSidebarCollapse = () => {
    isSidebarCollapsed.value = !isSidebarCollapsed.value;
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, String(isSidebarCollapsed.value));
    } catch (e) {
      console.error("Failed to persist sidebar collapsed state:", e);
    }
  };

  const openMobileSidebar = () => {
    isMobileSidebarOpen.value = true;
  };

  const closeMobileSidebar = () => {
    isMobileSidebarOpen.value = false;
  };

  const copyPublicLink = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(fullPublicUrl.value);
        isCopied.value = true;
        showToast({
          title: "URL Copied!",
          message: "Public website link copied to clipboard.",
          type: "success",
        });
        setTimeout(() => {
          isCopied.value = false;
        }, 2200);
      }
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  provide("changePage", changePage);
  provide("openMobileSidebar", openMobileSidebar);

  /* =========================================
   ON WEBSITE CREATED
  ========================================= */
  const onWebsiteCreated = () => {
    activePage.value = "dashboard";
    try {
      localStorage.setItem(STORAGE_KEY, "dashboard");
    } catch (error) {
      console.error("Failed to save active sidebar tab:", error);
    }
  };

  /* =========================================
   INITIALIZATION
  ========================================= */
  onMounted(async () => {
    try {
      const savedPage = localStorage.getItem(STORAGE_KEY);
      if (savedPage && componentMap[savedPage]) {
        activePage.value = savedPage;
      }

      const savedCollapsed = localStorage.getItem(COLLAPSED_STORAGE_KEY);
      if (savedCollapsed !== null) {
        isSidebarCollapsed.value = savedCollapsed === "true";
      }

      await Promise.allSettled([
        store.dispatch("fetchWebsiteDetails"),
        store.dispatch("fetchMySchool"),
      ]);
    } catch (error) {
      console.error("Failed to initialize web dashboard:", error);
    } finally {
      isInitializing.value = false;
    }
  });
</script>

<template>
  <!-- INITIALIZING SKELETON LOADER -->
  <div
    v-if="isInitializing && !isWebsiteCreated"
    class="h-full w-full min-h-[500px] flex flex-col items-center justify-center p-8 bg-surface-50 dark:bg-[#0B1120] text-gray-900 dark:text-[#F1F5F9]"
  >
    <div
      class="w-16 h-16 rounded-3xl bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-4 shadow-sm"
    >
      <svg
        class="w-8 h-8 animate-spin"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        />
      </svg>
    </div>
    <p class="text-sm font-semibold text-gray-600 dark:text-[#A6B4C8]">
      Initializing Web Portal Studio...
    </p>
  </div>

  <!-- IF WEBSITE NOT CREATED: SHOW FIRST TO CREATE -->
  <div
    v-else-if="!isWebsiteCreated"
    class="h-full w-full min-h-0 bg-surface-50 dark:bg-[#0B1120] text-gray-900 dark:text-[#F1F5F9] overflow-y-auto custom-scrollbar p-3 sm:p-6 lg:p-8"
  >
    <div class="w-full max-w-6xl mx-auto py-2 sm:py-6">
      <CreateWebsitePrompt
        :school-data="schoolData"
        @website-created="onWebsiteCreated"
      />
    </div>
  </div>

  <!-- IF WEBSITE CREATED: FULL PRODUCTION WEB STUDIO -->
  <div
    v-else
    class="flex flex-col h-full min-h-0 w-full max-w-full overflow-x-hidden bg-transparent dark:bg-[#0B1120] text-gray-900 dark:text-[#F1F5F9] space-y-2.5 sm:space-y-4"
  >
    <!-- ======================================================== -->
    <!-- MASTER COMMAND HEADER (Ultra-responsive Studio Bar) -->
    <!-- ======================================================== -->
    <header
      class="shrink-0 sticky top-0 z-30 bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-xl border border-slate-200/90 dark:border-[#263449] rounded-2xl shadow-xs px-2.5 sm:px-5 py-2 sm:py-3 transition-all duration-300"
    >
      <div class="flex items-center justify-between gap-2 sm:gap-3">
        <!-- LEFT: Mobile Menu Trigger + Branding & Active Breadcrumb -->
        <div class="flex items-center gap-2 sm:gap-3.5 min-w-0 flex-1">
          <!-- Mobile Drawer Trigger Button -->
          <button
            type="button"
            class="w-8 h-8 sm:w-9 sm:h-9 lg:hidden inline-flex items-center justify-center rounded-xl bg-slate-50 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-white border border-slate-200/90 dark:border-[#263449] shadow-xs transition-all cursor-pointer active:scale-95 shrink-0"
            @click="openMobileSidebar"
            aria-label="Open Studio Menu"
          >
            <Menu :size="16" class="shrink-0" />
          </button>

          <!-- School Logo Avatar -->
          <div
            class="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-tr from-blue-600 via-indigo-600 to-sky-500 text-white font-black text-xs sm:text-sm flex items-center justify-center shadow-md ring-2 ring-white dark:ring-[#263449] overflow-hidden shrink-0"
          >
            <img
              v-if="schoolLogo"
              :src="schoolLogo"
              :alt="schoolName"
              class="w-full h-full object-cover"
            />
            <span v-else>{{ schoolInitials }}</span>
          </div>

          <!-- Title Stack & Breadcrumbs -->
          <div class="min-w-0 flex-1">
            <div class="hidden sm:flex items-center gap-1.5 text-[10px] font-extrabold tracking-wider uppercase text-slate-400 dark:text-[#7E8DA5]">
              <span>EduManage Studio</span>
              <span>/</span>
              <span class="text-indigo-600 dark:text-blue-400">{{ activeMeta.category }}</span>
            </div>

            <div class="flex items-center gap-1.5 sm:gap-2">
              <component
                :is="activeMeta.icon"
                :size="15"
                class="text-indigo-600 dark:text-blue-400 shrink-0 hidden sm:inline"
              />
              <h1 class="text-xs sm:text-base font-extrabold text-slate-900 dark:text-[#F1F5F9] truncate leading-tight">
                {{ activeMeta.title }}
              </h1>

              <!-- Live Status Pill -->
              <span
                class="hidden md:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border transition-colors shrink-0"
                :class="isLive
                  ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/40'
                  : 'bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/40'"
              >
                <span
                  class="w-1.5 h-1.5 rounded-full"
                  :class="isLive ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'"
                />
                <span>{{ isLive ? 'Live' : 'Staging' }}</span>
              </span>
            </div>
          </div>
        </div>

        <!-- RIGHT: Quick Actions (Copy Link, Visit Site, Desktop Collapse) -->
        <div class="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
          <!-- Copy Public Link Button -->
          <button
            type="button"
            class="inline-flex items-center gap-1.5 px-2 py-1.5 sm:px-3 sm:py-2 rounded-xl text-xs font-bold border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-50 dark:hover:bg-[#1F2E47] transition shadow-xs cursor-pointer active:scale-95 shrink-0"
            :title="fullPublicUrl"
            @click="copyPublicLink"
          >
            <Check v-if="isCopied" :size="14" class="text-emerald-500 shrink-0" />
            <Copy v-else :size="14" class="text-slate-400 dark:text-[#7E8DA5] shrink-0" />
            <span class="hidden sm:inline">{{ isCopied ? 'Copied!' : 'Copy URL' }}</span>
          </button>

          <!-- Visit Live Website Button -->
          <a
            :href="fullPublicUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md shadow-blue-500/20 transition cursor-pointer active:scale-95 shrink-0"
            title="Open Live Public Website in New Tab"
          >
            <Globe :size="14" class="shrink-0" />
            <span class="hidden sm:inline">Visit Site</span>
            <ExternalLink :size="12" class="opacity-80 hidden sm:inline shrink-0" />
          </a>

          <!-- Desktop Sidebar Rail Mode Toggle -->
          <button
            type="button"
            class="hidden lg:flex p-2 rounded-xl text-slate-500 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-[#18243A] border border-slate-200/80 dark:border-[#263449] transition cursor-pointer shrink-0"
            :title="isSidebarCollapsed ? 'Expand Studio Sidebar' : 'Compact Studio Sidebar'"
            @click="toggleSidebarCollapse"
          >
            <PanelLeftOpen v-if="isSidebarCollapsed" :size="17" />
            <PanelLeftClose v-else :size="17" />
          </button>
        </div>
      </div>
    </header>

    <!-- ======================================================== -->
    <!-- MOBILE / TABLET QUICK-SCROLL PILL STRIP (lg:hidden) -->
    <!-- ======================================================== -->
    <nav
      ref="pillStripRef"
      class="lg:hidden shrink-0 flex items-center gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar py-0.5 px-0.5 select-none"
      aria-label="Quick section navigation"
    >
      <button
        v-for="item in mobilePillItems"
        :key="item.id"
        :data-pill-id="item.id"
        type="button"
        :class="[
          'inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all duration-200 cursor-pointer shrink-0 shadow-xs active:scale-95',
          activePage === item.id
            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/25 ring-1 ring-blue-400/40'
            : 'bg-white dark:bg-[#111C30] text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9] border border-slate-200/80 dark:border-[#263449]',
        ]"
        @click="changePage(item.id)"
      >
        <component
          :is="item.icon"
          :size="14"
          :stroke-width="activePage === item.id ? 2.4 : 1.8"
          :class="activePage === item.id ? 'text-white' : 'text-slate-400 dark:text-[#7E8DA5]'"
        />
        <span>{{ item.label }}</span>
      </button>
    </nav>

    <!-- ======================================================== -->
    <!-- WORKSPACE CANVAS (Sidebar + Responsive CMS Viewport) -->
    <!-- ======================================================== -->
    <div class="flex-1 min-h-0 flex items-stretch gap-3 lg:gap-4 overflow-hidden">
      <!-- DESKTOP STUDIO SIDEBAR -->
      <Sidebar
        :active-page="activePage"
        :is-collapsed="isSidebarCollapsed"
        :is-mobile-open="isMobileSidebarOpen"
        @change-page="changePage"
        @toggle-collapse="toggleSidebarCollapse"
        @close-mobile="closeMobileSidebar"
      />

      <!-- MAIN EDITING VIEWPORT CONTAINER -->
      <main
        ref="scrollContainerRef"
        class="flex-1 min-w-0 min-h-0 h-full overflow-y-auto overflow-x-hidden p-2.5 sm:p-4 lg:p-6 custom-scrollbar bg-white/70 dark:bg-[#111C30]/70 rounded-2xl border border-slate-200/80 dark:border-[#263449]/70 shadow-xs"
      >
        <div class="w-full max-w-[94rem] mx-auto pb-16">
          <transition name="fade-slide" mode="out-in">
            <component
              :is="currentComponent"
              :key="activePage"
              :active-page="activePage"
              @change-page="changePage"
            />
          </transition>
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
  /* =========================================
     TRANSITIONS
  ========================================= */
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  .fade-slide-enter-from {
    opacity: 0;
    transform: translateY(6px);
  }
  .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-6px);
  }

  /* =========================================
     HIDE SCROLLBAR HELPER
  ========================================= */
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  /* =========================================
     CUSTOM WORKSPACE SCROLLBAR
  ========================================= */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 9999px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  html.dark .custom-scrollbar {
    scrollbar-color: #263449 transparent;
  }
  html.dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #263449;
  }
  html.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #3b82f6;
  }
  .custom-scrollbar::-webkit-scrollbar:horizontal {
    display: none;
  }
</style>
